import { useCallback } from 'react';
import { useTabs } from '../../context/TabsContext';

const docTabFields = (docId, title) => ({
  type: 'doc',
  targetId: docId,
  label: title?.trim() ? title : 'Untitled Doc',
  icon: null,
  viewMode: null,
  customViewId: null,
});

const docsHomeFields = () => ({
  type: 'docs',
  targetId: null,
  label: 'Documents',
  icon: null,
  viewMode: null,
  customViewId: null,
  docStack: [],
  docScrollById: {},
});

/**
 * Keep one reusable Docs chip: the home tab and the open document are the
 * same tab. Opening a doc (or another doc) updates that chip in place.
 *
 * `showDocInTab` is for a user opening a doc (creates/reuses and focuses).
 * `syncActiveDocTab` only updates the already-active doc tab — it must not
 * steal focus back after the user clicks or closes another tab.
 */
export function useDocTab() {
  const { tabs, activeTab, activeTabId, openTab, updateTab, switchToTab, closeTab } = useTabs();

  const showDocInTab = useCallback((docId, title) => {
    if (!docId) return;

    const updates = docTabFields(docId, title);
    // Docs home and the open document are the same chip — don't spawn a second tab.
    const reusable = (activeTab?.type === 'doc' || activeTab?.type === 'docs')
      ? activeTab
      : [...tabs].reverse().find((tab) => tab.type === 'doc');

    if (reusable) {
      const sameDoc =
        reusable.type === 'doc' &&
        reusable.targetId === docId &&
        reusable.label === updates.label;
      if (!sameDoc) {
        updateTab(reusable.id, {
          ...updates,
          docStack: [docId],
          docScrollById:
            reusable.type === 'doc' && reusable.docScrollById
              ? reusable.docScrollById
              : {},
        });
      }
      if (activeTabId !== reusable.id) {
        switchToTab(reusable.id);
      }
      return;
    }

    openTab({
      type: 'doc',
      targetId: docId,
      label: updates.label,
      icon: null,
      viewMode: null,
      docStack: [docId],
      docScrollById: {},
    });
  }, [tabs, activeTab, activeTabId, openTab, updateTab, switchToTab]);

  const openDocInNewTab = useCallback((docId, title) => {
    if (!docId) return;
    const updates = docTabFields(docId, title);
    openTab({
      type: 'doc',
      targetId: docId,
      label: updates.label,
      icon: null,
      viewMode: null,
      allowDuplicate: true,
      docStack: [docId],
      docScrollById: {},
    });
  }, [openTab]);

  const syncActiveDocTab = useCallback((docId, title) => {
    if (!docId || activeTab?.type !== 'doc') return;
    const updates = docTabFields(docId, title);
    if (activeTab.targetId === docId && activeTab.label === updates.label) return;
    updateTab(activeTab.id, updates);
  }, [activeTab, updateTab]);

  /** "← Docs" / close sheet: this chip becomes the Docs home, not a leftover document. */
  const returnToDocsHome = useCallback(() => {
    if (activeTab?.type === 'doc') {
      const keepId = activeTab.id;
      updateTab(keepId, docsHomeFields());
      tabs.forEach((tab) => {
        if (tab.type === 'docs' && tab.id !== keepId) closeTab(tab.id);
      });
    } else {
      openTab({
        type: 'docs',
        targetId: null,
        label: 'Docs',
        icon: null,
        viewMode: null,
      });
    }
    requestAnimationFrame(() => {
      window.dispatchEvent(new CustomEvent('fokus:docs-home'));
    });
  }, [activeTab, tabs, updateTab, closeTab, openTab]);

  return { showDocInTab, openDocInNewTab, syncActiveDocTab, returnToDocsHome };
}
